import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Bookmark,
  CheckCircle2,
  Globe,
  Heart,
  MessageSquare,
  MoreHorizontal,
  Printer,
  Repeat,
  Send,
  Share2,
  Sparkles,
  ThumbsUp,
  User,
} from 'lucide-react';

export interface UniversalPreviewProps {
  content_type: string;
  content_body: string | React.ReactNode;
  subject?: string;
  /** Optional piece-level sibling flags for layout detection */
  piece?: {
    layout_type?: string;
    dynamic_layout?: boolean;
    matched_recipe?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

type AccentKey = 'indigo' | 'emerald' | 'amber' | 'sky' | 'rose';

type DynamicLayoutSpec = {
  dynamic_layout?: boolean;
  intent_type?: string;
  layout_type?: string;
  accent_color?: AccentKey | string;
  eyebrow?: string;
  headline?: string;
  subheadline?: string;
  summary?: string;
  quote?: string;
  attribution?: string;
  left_label?: string;
  right_label?: string;
  cta_label?: string;
  metrics?: Array<string | Record<string, unknown>>;
  columns?: Array<Record<string, unknown>>;
  items?: Array<string | Record<string, unknown>>;
  comparison_items?: Array<Record<string, unknown>>;
  feature_items?: Array<string | Record<string, unknown>>;
  callouts?: Array<string | Record<string, unknown>>;
  sections?: Array<Record<string, unknown>>;
  event_details?: Array<Record<string, unknown>>;
  genui_html?: string;
};

type AccentTheme = {
  shell: string;
  glow: string;
  badge: string;
  soft: string;
  solid: string;
  text: string;
  border: string;
  button: string;
};

const ACCENT_THEMES: Record<AccentKey, AccentTheme> = {
  indigo: {
    shell: 'from-slate-950 via-indigo-950 to-slate-950',
    glow: 'bg-indigo-500/20',
    badge: 'bg-indigo-500/15 text-indigo-200 border-indigo-400/30',
    soft: 'bg-indigo-500/10',
    solid: 'bg-indigo-500',
    text: 'text-indigo-200',
    border: 'border-indigo-400/25',
    button: 'from-indigo-500 via-indigo-400 to-sky-400',
  },
  emerald: {
    shell: 'from-slate-950 via-emerald-950 to-slate-950',
    glow: 'bg-emerald-500/20',
    badge: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30',
    soft: 'bg-emerald-500/10',
    solid: 'bg-emerald-500',
    text: 'text-emerald-200',
    border: 'border-emerald-400/25',
    button: 'from-emerald-500 via-teal-400 to-sky-400',
  },
  amber: {
    shell: 'from-slate-950 via-amber-950 to-slate-950',
    glow: 'bg-amber-500/20',
    badge: 'bg-amber-500/15 text-amber-100 border-amber-300/30',
    soft: 'bg-amber-500/10',
    solid: 'bg-amber-500',
    text: 'text-amber-100',
    border: 'border-amber-300/25',
    button: 'from-amber-500 via-orange-400 to-rose-400',
  },
  sky: {
    shell: 'from-slate-950 via-sky-950 to-slate-950',
    glow: 'bg-sky-500/20',
    badge: 'bg-sky-500/15 text-sky-100 border-sky-300/30',
    soft: 'bg-sky-500/10',
    solid: 'bg-sky-500',
    text: 'text-sky-100',
    border: 'border-sky-300/25',
    button: 'from-sky-500 via-cyan-400 to-blue-400',
  },
  rose: {
    shell: 'from-slate-950 via-rose-950 to-slate-950',
    glow: 'bg-rose-500/20',
    badge: 'bg-rose-500/15 text-rose-100 border-rose-300/30',
    soft: 'bg-rose-500/10',
    solid: 'bg-rose-500',
    text: 'text-rose-100',
    border: 'border-rose-300/25',
    button: 'from-rose-500 via-pink-400 to-orange-400',
  },
};

function cleanFences(htmlStr: string) {
  return htmlStr.replace(/```html\n?|```/g, '').trim();
}

/** Emma wireframes use 100vh/100vw which break inside the Step 8 preview card. */
function sanitizeEmbeddedPreviewHtml(html: string) {
  return html
    .replace(/min-height\s*:\s*100vh/gi, 'min-height: auto')
    .replace(/height\s*:\s*100vh/gi, 'min-height: 480px')
    .replace(/width\s*:\s*100vw/gi, 'width: 100%')
    .replace(/position\s*:\s*fixed/gi, 'position: relative');
}

function resolveGenuiHtml(
  source: Record<string, unknown>,
  piece?: { matched_recipe?: Record<string, unknown> },
): string {
  const fromBody = source.genui_html || (source.matched_recipe as any)?.visual_template;
  const fromPiece = (piece?.matched_recipe as any)?.visual_template;
  const raw = String(fromBody || fromPiece || '');
  const cleaned = cleanFences(raw);
  return cleaned ? sanitizeEmbeddedPreviewHtml(cleaned) : '';
}

function buildGenuiLayoutSpec(
  source: Record<string, unknown>,
  piece?: { matched_recipe?: Record<string, unknown> },
): DynamicLayoutSpec | null {
  const genuiHtml = resolveGenuiHtml(source, piece);
  if (!genuiHtml) {
    return null;
  }
  return {
    dynamic_layout: true,
    intent_type: String(source.intent_type || 'dynamic_auto'),
    layout_type: 'genui_html',
    genui_html: genuiHtml,
    accent_color: (source.accent_color as string) || 'indigo',
    headline: source.headline as string | undefined,
    subheadline: source.subheadline as string | undefined,
    summary: source.summary as string | undefined,
    callouts: source.callouts as DynamicLayoutSpec['callouts'],
    cta_label: source.cta_label as string | undefined,
  };
}

function parseDynamicLayout(
  body: any,
  piece?: { layout_type?: string; dynamic_layout?: boolean; matched_recipe?: Record<string, unknown>; [key: string]: unknown },
): DynamicLayoutSpec | null {
  if (!body && !piece) return null;

  // 1. Handle object structures
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const hasVisualTemplate = !!body.matched_recipe?.visual_template;
    const isDynamic =
      body.layout_type === 'genui_html' ||
      body.dynamic_layout === true ||
      hasVisualTemplate;

    if (isDynamic) {
      const genuiSpec = buildGenuiLayoutSpec(body as Record<string, unknown>, piece);
      if (genuiSpec) return genuiSpec;
      if (body.headline || body.callouts || body.summary) {
        return body as DynamicLayoutSpec;
      }
    }
  }

  // 2. Handle string structures (JSON or raw HTML fallback)
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed === 'object') {
        const hasVisualTemplate = !!parsed.matched_recipe?.visual_template;
        const isDynamic =
          parsed.layout_type === 'genui_html' ||
          parsed.dynamic_layout === true ||
          hasVisualTemplate;

        if (isDynamic) {
          const genuiSpec = buildGenuiLayoutSpec(parsed as Record<string, unknown>, piece);
          if (genuiSpec) return genuiSpec;
          if (parsed.headline || parsed.callouts || parsed.summary) {
            return parsed as DynamicLayoutSpec;
          }
        }
      }
    } catch {
      // String is not JSON. Check if it contains visual HTML style blocks or div markers
      const trimmedBody = body.trim();
      const hasHtmlSignatures = 
        trimmedBody.startsWith('<style') || 
        trimmedBody.startsWith('<div') || 
        trimmedBody.includes('style=') || 
        trimmedBody.includes('<div') || 
        trimmedBody.includes('<style');

      if (hasHtmlSignatures) {
        const sanitized = sanitizeEmbeddedPreviewHtml(cleanFences(body));
        if (sanitized) {
          return {
            dynamic_layout: true,
            layout_type: 'genui_html',
            genui_html: sanitized,
          };
        }
      }
    }
  }

  // 3. Piece-level sibling flag: body may be empty JSON but matched_recipe still has wireframe HTML.
  if (piece) {
    const pieceHasVisualTemplate = !!(piece.matched_recipe as any)?.visual_template;
    const pieceIsDynamic =
      piece.layout_type === 'genui_html' ||
      piece.dynamic_layout === true ||
      pieceHasVisualTemplate;

    if (pieceIsDynamic && pieceHasVisualTemplate) {
      return buildGenuiLayoutSpec({}, piece);
    }
  }

  return null;
}

function normalizeAccent(accent: unknown): AccentKey {
  const key = String(accent || '').toLowerCase().trim() as AccentKey;
  return key in ACCENT_THEMES ? key : 'indigo';
}

function getMetricParts(metric: string | Record<string, unknown>): {
  label: string;
  value: string;
  detail: string;
} {
  if (typeof metric === 'string') {
    return { label: metric, value: metric, detail: '' };
  }

  return {
    label: String(metric.label || metric.title || metric.name || 'Metric'),
    value: String(metric.value || metric.stat || metric.number || metric.label || ''),
    detail: String(metric.detail || metric.context || metric.description || ''),
  };
}

function getItemParts(item: string | Record<string, unknown>): {
  title: string;
  value: string;
  detail: string;
  badge: string;
} {
  if (typeof item === 'string') {
    return { title: item, value: '', detail: '', badge: '' };
  }

  return {
    title: String(item.title || item.label || item.name || 'Item'),
    value: String(item.value || item.stat || item.outcome || ''),
    detail: String(item.detail || item.description || item.text || item.copy || ''),
    badge: String(item.badge || item.tag || ''),
  };
}

function getColumnParts(column: Record<string, unknown>) {
  const title = String(column.title || column.heading || column.name || 'Column');
  const subtitle = String(column.subtitle || column.summary || '');
  const items = Array.isArray(column.items) ? column.items : [];
  return { title, subtitle, items };
}

function getSectionParts(section: Record<string, unknown>) {
  return {
    title: String(section.title || section.heading || section.name || 'Section'),
    summary: String(section.summary || section.description || ''),
    bullets: Array.isArray(section.bullets) ? section.bullets : [],
  };
}

function formatKeyName(key: string) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Process HTML to replace [SRC-XXX] citation markers with clickable badge spans.
 * Returns the processed HTML string.
 */
function processCitationMarkers(html: string): string {
  return html.replace(
    /\[SRC-(\d+)\]/g,
    (_match, idx) =>
      `<span class="cite-badge" data-cite-idx="${idx}" style="display:inline-block;padding:1px 6px;border-radius:8px;font-size:10px;font-weight:600;background:#EEEDFE;color:#3C3489;cursor:pointer;margin:0 2px;vertical-align:super;font-family:monospace;">SRC-${idx}</span>`
  );
}

/**
 * Inline citation popover for Custom Content genui_html preview.
 * Renders clickable [SRC-XXX] badges with popover on click.
 */
function CitationPopoverLayer({ html, claimMappings }: { html: string; claimMappings?: Array<Record<string, any>> }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [popover, setPopover] = useState<{ x: number; y: number; cite: Record<string, any> } | null>(null);

  const handleClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const badge = target.closest('.cite-badge') as HTMLElement | null;
    if (!badge) {
      setPopover(null);
      return;
    }
    const idx = parseInt(badge.dataset.citeIdx || '0', 10);
    const cite = claimMappings?.find((c: any) => c.citation_index === idx || c.index === idx);
    if (!cite) {
      setPopover(null);
      return;
    }
    const rect = badge.getBoundingClientRect();
    setPopover({ x: rect.left, y: rect.bottom + 8, cite });
  }, [claimMappings]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popover && !(e.target as HTMLElement).closest('.citation-popover-box')) {
        setPopover(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popover]);

  const processedHtml = processCitationMarkers(html);

  return (
    <div ref={containerRef} onClick={handleClick} className="relative">
      <div dangerouslySetInnerHTML={{ __html: processedHtml }} />
      {popover && (
        <div
          className="citation-popover-box fixed z-50 bg-white border border-gray-200 rounded-xl p-4 max-w-[380px] shadow-xl text-xs"
          style={{ top: popover.y, left: Math.min(popover.x, window.innerWidth - 400) }}
        >
          <button
            onClick={() => setPopover(null)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-sm"
          >
            ×
          </button>
          <div className="font-semibold text-gray-900 mb-1">{popover.cite.source_title || popover.cite.title || 'Source'}</div>
          {popover.cite.paragraph_ref && (
            <div className="text-gray-500 mb-1">{popover.cite.paragraph_ref}</div>
          )}
          {popover.cite.source_excerpt && (
            <div className="italic text-gray-600 mb-2">"{popover.cite.source_excerpt}..."</div>
          )}
          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700">
            {popover.cite.citation_type === 'verified' ? 'Source-backed' : 'Source-backed'}
          </span>
          {popover.cite.similarity_score && (
            <span className="ml-2 text-gray-400 text-[10px]">
              {Math.round(popover.cite.similarity_score * 100)}% match
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function renderDynamicLayout(spec: DynamicLayoutSpec, subject?: string, claimMappings?: Array<Record<string, any>>) {
  const layoutType = String(spec.layout_type || 'hero_sheet').toLowerCase();

  if (layoutType === 'genui_html' && spec.genui_html) {
    // Sanitize: strip script tags, event handlers, iframes, and dangerous attributes
    const sanitized = String(spec.genui_html)
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      .replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '')
      .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
      .replace(/javascript\s*:/gi, '');

    return (
      <div className="w-full rounded-2xl overflow-hidden shadow-md" style={{contain: 'layout'}}>
        <style dangerouslySetInnerHTML={{ __html: `
          /* ── Containment: prevent any child from overflowing the card ── */
          .genui-html-wrapper {
            display: block;
            width: 100%;
            overflow: hidden;
            box-sizing: border-box;
          }
          .genui-html-wrapper *,
          .genui-html-wrapper *::before,
          .genui-html-wrapper *::after {
            box-sizing: border-box;
            max-width: 100%;
            word-break: break-word;
            overflow-wrap: break-word;
          }
          .genui-html-wrapper img,
          .genui-html-wrapper video {
            max-width: 100% !important;
            height: auto !important;
          }

          /* ── Heading safety net: headings inherit their container's color ── */
          .genui-html-wrapper h1,
          .genui-html-wrapper h2,
          .genui-html-wrapper h3,
          .genui-html-wrapper h4,
          .genui-html-wrapper h5,
          .genui-html-wrapper h6 {
            color: inherit;
          }

          /* ── Tailwind color fallbacks (NO !important — LLM CSS takes precedence) ── */
          .genui-html-wrapper .bg-white      { background-color: #ffffff; }
          .genui-html-wrapper .bg-black      { background-color: #000000; }
          .genui-html-wrapper .bg-slate-50   { background-color: #f8fafc; }
          .genui-html-wrapper .bg-slate-100  { background-color: #f1f5f9; }
          .genui-html-wrapper .bg-slate-200  { background-color: #e2e8f0; }
          .genui-html-wrapper .bg-slate-700  { background-color: #334155; }
          .genui-html-wrapper .bg-slate-800  { background-color: #1e293b; }
          .genui-html-wrapper .bg-slate-900  { background-color: #0f172a; }
          .genui-html-wrapper .bg-slate-950  { background-color: #020617; }
          .genui-html-wrapper .bg-gray-50    { background-color: #f9fafb; }
          .genui-html-wrapper .bg-gray-100   { background-color: #f3f4f6; }
          .genui-html-wrapper .bg-gray-200   { background-color: #e5e7eb; }
          .genui-html-wrapper .bg-gray-800   { background-color: #1f2937; }
          .genui-html-wrapper .bg-gray-900   { background-color: #111827; }
          .genui-html-wrapper .bg-indigo-50  { background-color: #eef2ff; }
          .genui-html-wrapper .bg-indigo-500 { background-color: #6366f1; }
          .genui-html-wrapper .bg-indigo-600 { background-color: #4f46e5; }
          .genui-html-wrapper .bg-indigo-900 { background-color: #312e81; }
          .genui-html-wrapper .bg-blue-50    { background-color: #eff6ff; }
          .genui-html-wrapper .bg-blue-500   { background-color: #3b82f6; }
          .genui-html-wrapper .bg-blue-600   { background-color: #2563eb; }
          .genui-html-wrapper .bg-blue-900   { background-color: #1e3a8a; }
          .genui-html-wrapper .bg-sky-50     { background-color: #f0f9ff; }
          .genui-html-wrapper .bg-sky-500    { background-color: #0ea5e9; }
          .genui-html-wrapper .bg-sky-900    { background-color: #0c4a6e; }
          .genui-html-wrapper .bg-emerald-50  { background-color: #ecfdf5; }
          .genui-html-wrapper .bg-emerald-500 { background-color: #10b981; }
          .genui-html-wrapper .bg-emerald-600 { background-color: #059669; }
          .genui-html-wrapper .bg-emerald-900 { background-color: #064e3b; }
          .genui-html-wrapper .bg-amber-50   { background-color: #fffbeb; }
          .genui-html-wrapper .bg-amber-500  { background-color: #f59e0b; }
          .genui-html-wrapper .bg-rose-50    { background-color: #fff1f2; }
          .genui-html-wrapper .bg-rose-500   { background-color: #f43f5e; }
          .genui-html-wrapper .bg-rose-900   { background-color: #881337; }
          .genui-html-wrapper .bg-purple-900 { background-color: #4a1d96; }

          /* ── Text colors (WITH !important for contrast enforcement) ── */
          .genui-html-wrapper .text-white      { color: #ffffff !important; }
          .genui-html-wrapper .text-black      { color: #000000 !important; }
          .genui-html-wrapper .text-slate-50   { color: #f8fafc !important; }
          .genui-html-wrapper .text-slate-100  { color: #f1f5f9 !important; }
          .genui-html-wrapper .text-slate-200  { color: #e2e8f0 !important; }
          .genui-html-wrapper .text-slate-300  { color: #cbd5e1 !important; }
          .genui-html-wrapper .text-slate-400  { color: #94a3b8 !important; }
          .genui-html-wrapper .text-slate-600  { color: #475569 !important; }
          .genui-html-wrapper .text-slate-700  { color: #334155 !important; }
          .genui-html-wrapper .text-slate-800  { color: #1e293b !important; }
          .genui-html-wrapper .text-slate-900  { color: #0f172a !important; }
          .genui-html-wrapper .text-gray-400   { color: #9ca3af !important; }
          .genui-html-wrapper .text-gray-500   { color: #6b7280 !important; }
          .genui-html-wrapper .text-gray-600   { color: #4b5563 !important; }
          .genui-html-wrapper .text-gray-700   { color: #374151 !important; }
          .genui-html-wrapper .text-gray-800   { color: #1f2937 !important; }
          .genui-html-wrapper .text-gray-900   { color: #111827 !important; }
          .genui-html-wrapper .text-indigo-300 { color: #a5b4fc !important; }
          .genui-html-wrapper .text-indigo-400 { color: #818cf8 !important; }
          .genui-html-wrapper .text-indigo-500 { color: #6366f1 !important; }
          .genui-html-wrapper .text-blue-100   { color: #dbeafe !important; }
          .genui-html-wrapper .text-blue-400   { color: #60a5fa !important; }
          .genui-html-wrapper .text-sky-100    { color: #e0f2fe !important; }
          .genui-html-wrapper .text-sky-300    { color: #7dd3fc !important; }
          .genui-html-wrapper .text-sky-400    { color: #38bdf8 !important; }
          .genui-html-wrapper .text-emerald-300 { color: #6ee7b7 !important; }
          .genui-html-wrapper .text-emerald-400 { color: #34d399 !important; }
          .genui-html-wrapper .text-amber-100  { color: #fef3c7 !important; }
          .genui-html-wrapper .text-amber-300  { color: #fcd34d !important; }
          .genui-html-wrapper .text-amber-400  { color: #fbbf24 !important; }
          .genui-html-wrapper .text-rose-100   { color: #ffe4e6 !important; }
          .genui-html-wrapper .text-rose-300   { color: #fda4af !important; }
          .genui-html-wrapper .text-rose-400   { color: #fb7185 !important; }
          .genui-html-wrapper .text-cyan-200   { color: #a5f3fc !important; }
          .genui-html-wrapper .text-purple-100 { color: #f3e8ff !important; }

          /* ── List inheritance ── */
          .genui-html-wrapper ul,
          .genui-html-wrapper ol,
          .genui-html-wrapper li {
            color: inherit;
          }
        `}} />
        <CitationPopoverLayer html={sanitized} claimMappings={claimMappings} />
      </div>
    );
  }

  const accent = normalizeAccent(spec.accent_color);
  const theme = ACCENT_THEMES[accent];
  const headline = spec.headline || subject || 'Premium Campaign Concept';
  const metrics = Array.isArray(spec.metrics) ? spec.metrics.slice(0, 4) : [];
  const columns = Array.isArray(spec.columns) ? spec.columns.slice(0, 3) : [];
  const callouts = Array.isArray(spec.callouts) ? spec.callouts.slice(0, 3) : [];
  const comparisonItems = Array.isArray(spec.comparison_items) ? spec.comparison_items.slice(0, 4) : [];
  const sections = Array.isArray(spec.sections) ? spec.sections.slice(0, 4) : [];
  const eventDetails = Array.isArray(spec.event_details) ? spec.event_details.slice(0, 4) : [];
  const featureItems = Array.isArray(spec.feature_items)
    ? spec.feature_items.slice(0, 6)
    : Array.isArray(spec.items)
      ? spec.items.slice(0, 6)
      : [];

  const shell = (
    <div
      className={`relative overflow-hidden rounded-2xl border ${theme.border} bg-gradient-to-br ${theme.shell} text-white shadow-2xl`}
      style={{ fontFamily: 'Outfit, ui-sans-serif, system-ui, sans-serif' }}
    >
      <div className={`pointer-events-none absolute -left-12 top-10 h-40 w-40 rounded-full blur-3xl ${theme.glow}`} />
      <div className={`pointer-events-none absolute right-0 top-0 h-48 w-48 rounded-full blur-3xl ${theme.glow}`} />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.12),transparent_35%),linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:auto,24px_24px,24px_24px]" />
      <div className="relative z-10 p-6 md:p-7">
        <div className="mb-5 flex items-center justify-between gap-3">
          <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${theme.badge}`}>
            <Sparkles className="h-3.5 w-3.5" />
            {spec.eyebrow || 'Custom Campaign Layout'}
          </div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/55">
            Interactive Preview
          </div>
        </div>

        {layoutType === 'comparison_grid' && (
          <div className="space-y-5">
            <div>
              <h2 className="max-w-3xl text-3xl font-black leading-tight md:text-[2.2rem]">
                {headline}
              </h2>
              {spec.subheadline && (
                <p className="mt-3 max-w-2xl text-sm leading-6 text-white/72 whitespace-pre-wrap">
                  {spec.subheadline}
                </p>
              )}
            </div>
            {(spec.left_label || spec.right_label) && (
              <div className="grid gap-4 md:grid-cols-[1.1fr_1fr_1fr_0.8fr]">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/45">Dimension</div>
                <div className={`text-xs font-semibold uppercase tracking-[0.2em] ${theme.text}`}>{spec.left_label || 'Option A'}</div>
                <div className={`text-xs font-semibold uppercase tracking-[0.2em] ${theme.text}`}>{spec.right_label || 'Option B'}</div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/45">Verdict</div>
              </div>
            )}
            <div className="space-y-3">
              {comparisonItems.map((entry, index) => {
                const dimension = String(entry.dimension || entry.title || `Dimension ${index + 1}`);
                const leftValue = String(entry.left_value || entry.left || entry.value || '');
                const rightValue = String(entry.right_value || entry.right || '');
                const verdict = String(entry.verdict || entry.badge || '');
                return (
                  <div
                    key={`${dimension}-${index}`}
                    className="grid gap-3 rounded-2xl border border-white/10 bg-white/8 p-4 backdrop-blur-xl md:grid-cols-[1.1fr_1fr_1fr_0.8fr]"
                  >
                    <div>
                      <p className="text-sm font-bold">{dimension}</p>
                    </div>
                    <div className="rounded-xl border border-white/8 bg-black/10 px-3 py-2.5 text-sm text-white/82">
                      {leftValue}
                    </div>
                    <div className="rounded-xl border border-white/8 bg-black/10 px-3 py-2.5 text-sm text-white/82">
                      {rightValue}
                    </div>
                    <div className="flex items-start md:justify-end">
                      {verdict && (
                        <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${theme.badge}`}>
                          {verdict}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {comparisonItems.length === 0 && (
                <div className="grid gap-4 md:grid-cols-2">
                  {columns.map((entry, index) => {
                    const { title, subtitle, items } = getColumnParts(entry);
                    return (
                      <div
                        key={`${title}-${index}`}
                        className="rounded-2xl border border-white/10 bg-white/8 p-4 backdrop-blur-xl"
                      >
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <h3 className="text-base font-bold">{title}</h3>
                          <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${theme.badge}`}>
                            Option {index + 1}
                          </span>
                        </div>
                        {subtitle && <p className="mb-3 text-sm text-white/70">{subtitle}</p>}
                        <div className="space-y-2">
                          {items.map((item, itemIndex) => {
                            const parts = getItemParts(item as string | Record<string, unknown>);
                            return (
                              <div key={`${parts.title}-${itemIndex}`} className="rounded-xl border border-white/8 bg-black/10 px-3 py-2.5">
                                <div className="flex items-start justify-between gap-3">
                                  <p className="text-sm font-semibold">{parts.title}</p>
                                  {parts.value && <span className={`text-xs font-bold ${theme.text}`}>{parts.value}</span>}
                                </div>
                                {parts.detail && <p className="mt-1 text-xs leading-5 text-white/65">{parts.detail}</p>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {layoutType === 'newsletter_digest' && (
          <div className="space-y-5">
            <div className="grid gap-5 md:grid-cols-[1.35fr_0.9fr]">
              <div>
                <h2 className="max-w-3xl text-3xl font-black leading-tight text-white md:text-[2.2rem]">
                  {headline}
                </h2>
                {(spec.subheadline || spec.summary) && (
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-white/72 whitespace-pre-wrap">
                    {spec.subheadline || spec.summary}
                  </p>
                )}
              </div>
              <div className="rounded-3xl border border-white/10 bg-white/8 p-4 backdrop-blur-xl">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55">
                  This Edition
                </p>
                <div className="mt-3 space-y-2">
                  {callouts.map((callout, index) => {
                    const parts = getItemParts(callout as string | Record<string, unknown>);
                    return (
                      <div key={`${parts.title}-${index}`} className="rounded-2xl border border-white/8 bg-black/10 px-3 py-2.5">
                        <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${theme.text}`}>{parts.title}</p>
                        <p className="mt-1 text-xs leading-5 text-white/68">{parts.detail || parts.value || parts.title}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {sections.map((section, index) => {
                const parts = getSectionParts(section);
                return (
                  <div key={`${parts.title}-${index}`} className="rounded-2xl border border-white/10 bg-white/8 p-4 backdrop-blur-xl">
                    <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${theme.text}`}>{parts.title}</p>
                    {parts.summary && <p className="mt-2 text-sm leading-6 text-white/75">{parts.summary}</p>}
                    {parts.bullets.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {parts.bullets.map((bullet, bulletIndex) => (
                          <div key={`${parts.title}-bullet-${bulletIndex}`} className="flex gap-2 text-sm text-white/76">
                            <span className={theme.text}>•</span>
                            <span>{String(bullet)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {layoutType === 'poster_spotlight' && (
          <div className="space-y-6 text-center">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-4xl font-black leading-tight text-white md:text-[2.7rem]">
                {headline}
              </h2>
              {spec.subheadline && (
                <p className="mt-4 text-base leading-7 text-white/76 whitespace-pre-wrap">
                  {spec.subheadline}
                </p>
              )}
            </div>
            <div className="grid gap-4 md:grid-cols-4">
              {eventDetails.map((detail, index) => {
                const parts = getItemParts(detail);
                return (
                  <div key={`${parts.title}-${index}`} className="rounded-2xl border border-white/10 bg-white/8 px-4 py-4 backdrop-blur-xl">
                    <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${theme.text}`}>{parts.title}</p>
                    <p className="mt-2 text-base font-bold text-white">{parts.value || parts.detail}</p>
                  </div>
                );
              })}
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              {callouts.map((callout, index) => {
                const parts = getItemParts(callout as string | Record<string, unknown>);
                return (
                  <div key={`${parts.title}-${index}`} className="rounded-2xl border border-white/10 bg-black/15 px-4 py-3 text-left">
                    <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${theme.text}`}>{parts.title}</p>
                    <p className="mt-2 text-sm leading-6 text-white/74">{parts.detail || parts.value || parts.title}</p>
                  </div>
                );
              })}
            </div>
            <div className={`mx-auto inline-flex rounded-full bg-gradient-to-r px-5 py-3 text-xs font-black uppercase tracking-[0.2em] text-white shadow-lg ${theme.button}`}>
              {spec.cta_label || 'Reserve Your Spot'}
            </div>
          </div>
        )}

        {layoutType === 'executive_card' && (
          <div className="space-y-5">
            <div className="grid gap-6 md:grid-cols-[1.3fr_0.8fr]">
              <div>
                <h2 className="max-w-3xl text-3xl font-black leading-tight text-white md:text-[2.15rem]">
                  {headline}
                </h2>
                {spec.summary && (
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-white/72 whitespace-pre-wrap">
                    {spec.summary}
                  </p>
                )}
              </div>
              <div className="rounded-3xl border border-white/10 bg-white/8 p-4 backdrop-blur-xl">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55">
                  Recommended Action
                </p>
                <p className="mt-2 text-base font-bold">{spec.cta_label || 'Review next step'}</p>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              {metrics.map((metric, index) => {
                const parts = getMetricParts(metric);
                return (
                  <div key={`${parts.label}-${index}`} className="rounded-2xl border border-white/10 bg-white/8 px-4 py-3 backdrop-blur-xl">
                    <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${theme.text}`}>{parts.label}</p>
                    <p className="mt-2 text-2xl font-black">{parts.value}</p>
                    {parts.detail && <p className="mt-1 text-xs leading-5 text-white/65">{parts.detail}</p>}
                  </div>
                );
              })}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {callouts.map((callout, index) => {
                const parts = getItemParts(callout as string | Record<string, unknown>);
                return (
                  <div key={`${parts.title}-${index}`} className="rounded-2xl border border-white/10 bg-black/15 px-4 py-3">
                    <p className="text-sm font-semibold">{parts.title}</p>
                    <p className="mt-1 text-xs leading-5 text-white/68">{parts.detail || parts.value || parts.title}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {layoutType === 'infographic_card' && (
          <div className="space-y-6">
            <div>
              <h2 className="max-w-3xl text-3xl font-black leading-tight text-white md:text-[2.2rem]">
                {headline}
              </h2>
              {spec.summary && (
                <p className="mt-3 max-w-2xl text-sm leading-6 text-white/72">
                  {spec.summary}
                </p>
              )}
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {metrics.map((metric, index) => {
                const parts = getMetricParts(metric);
                return (
                  <div key={`${parts.label}-${index}`} className="rounded-2xl border border-white/10 bg-white/8 p-4 text-center backdrop-blur-xl">
                    <div className={`mx-auto mb-3 flex h-20 w-20 items-center justify-center rounded-full border border-white/10 ${theme.soft} text-xl font-black`}>
                      {parts.value}
                    </div>
                    <p className="text-sm font-semibold">{parts.label}</p>
                    {parts.detail && <p className="mt-1 text-xs leading-5 text-white/65">{parts.detail}</p>}
                  </div>
                );
              })}
            </div>
            {callouts.length > 0 && (
              <div className="grid gap-3 md:grid-cols-2">
                {callouts.map((callout, index) => {
                  const parts = getItemParts(callout as string | Record<string, unknown>);
                  return (
                    <div key={`${parts.title}-${index}`} className="rounded-2xl border border-white/10 bg-black/15 px-4 py-3">
                      <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${theme.text}`}>{parts.title}</p>
                      <p className="mt-2 text-sm leading-6 text-white/78">{parts.detail || parts.value || parts.title}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {layoutType === 'testimonial' && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55">
              <CheckCircle2 className={`h-4 w-4 ${theme.text}`} />
              Verified Customer
            </div>
            <blockquote className="rounded-[28px] border border-white/10 bg-white/8 p-6 text-left backdrop-blur-xl">
              <p className="text-2xl font-black leading-tight md:text-[2rem]">
                "{spec.quote || spec.summary || headline}"
              </p>
              {(spec.attribution || spec.subheadline) && (
                <footer className="mt-5 text-sm font-medium text-white/68">
                  {spec.attribution || spec.subheadline}
                </footer>
              )}
            </blockquote>
            {callouts.length > 0 && (
              <div className="grid gap-3 md:grid-cols-3">
                {callouts.map((callout, index) => {
                  const parts = getItemParts(callout as string | Record<string, unknown>);
                  return (
                    <div key={`${parts.title}-${index}`} className="rounded-2xl border border-white/10 bg-black/15 px-4 py-3">
                      <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${theme.text}`}>{parts.title || `Highlight ${index + 1}`}</p>
                      <p className="mt-2 text-sm leading-6 text-white/75">{parts.detail || parts.value || parts.title}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {layoutType === 'feature_list' && (
          <div className="space-y-5">
            <div>
              <h2 className="max-w-3xl text-3xl font-black leading-tight text-white md:text-[2.2rem]">
                {headline}
              </h2>
              {spec.subheadline && (
                <p className="mt-3 max-w-2xl text-sm leading-6 text-white/72 whitespace-pre-wrap">
                  {spec.subheadline}
                </p>
              )}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {featureItems.map((feature, index) => {
                const parts = getItemParts(feature);
                return (
                  <div key={`${parts.title}-${index}`} className="flex gap-3 rounded-2xl border border-white/10 bg-white/8 px-4 py-3 backdrop-blur-xl">
                    <div className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${theme.soft}`}>
                      <CheckCircle2 className={`h-4 w-4 ${theme.text}`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{parts.title}</p>
                      {(parts.detail || parts.value) && (
                        <p className="mt-1 text-xs leading-5 text-white/68">{parts.detail || parts.value}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {layoutType === 'hero_sheet' && (
          <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-[1.5fr_1fr]">
              <div>
                <h2 className="text-3xl font-black leading-tight text-white md:text-[2.35rem]">
                  {headline}
                </h2>
                {(spec.subheadline || spec.summary) && (
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-white/72 whitespace-pre-wrap">
                    {spec.subheadline || spec.summary}
                  </p>
                )}
              </div>
              <div className="rounded-3xl border border-white/10 bg-white/8 p-4 backdrop-blur-xl">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55">
                  Next Step
                </p>
                <p className="mt-2 text-base font-bold text-white">
                  {spec.cta_label || 'Schedule the co-selling follow-up'}
                </p>
                <div className={`mt-4 inline-flex rounded-full bg-gradient-to-r px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white shadow-lg ${theme.button}`}>
                  {spec.cta_label || 'Open CTA'}
                </div>
              </div>
            </div>
            {metrics.length > 0 && (
              <div className="grid gap-3 md:grid-cols-3">
                {metrics.map((metric, index) => {
                  const parts = getMetricParts(metric);
                  return (
                    <div key={`${parts.label}-${index}`} className="rounded-2xl border border-white/10 bg-white/8 px-4 py-3 backdrop-blur-xl">
                      <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${theme.text}`}>{parts.label}</p>
                      <p className="mt-2 text-2xl font-black">{parts.value}</p>
                      {parts.detail && <p className="mt-1 text-xs leading-5 text-white/65">{parts.detail}</p>}
                    </div>
                  );
                })}
              </div>
            )}
            {columns.length > 0 && (
              <div className="grid gap-4 md:grid-cols-3">
                {columns.map((column, index) => {
                  const { title, subtitle, items } = getColumnParts(column);
                  return (
                    <div key={`${title}-${index}`} className="rounded-2xl border border-white/10 bg-black/15 p-4">
                      <p className="text-base font-bold">{title}</p>
                      {subtitle && <p className="mt-1 text-xs leading-5 text-white/65">{subtitle}</p>}
                      <div className="mt-4 space-y-2">
                        {items.map((item, itemIndex) => {
                          const parts = getItemParts(item as string | Record<string, unknown>);
                          return (
                            <div key={`${parts.title}-${itemIndex}`} className="rounded-xl border border-white/8 bg-white/6 px-3 py-2.5">
                              <div className="flex items-start justify-between gap-3">
                                <p className="text-sm font-semibold">{parts.title}</p>
                                {parts.badge && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${theme.badge}`}>{parts.badge}</span>}
                              </div>
                              {(parts.detail || parts.value) && (
                                <p className="mt-1 text-xs leading-5 text-white/68">{parts.detail || parts.value}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* --- DYNAMIC FALLBACK RENDERER --- */}
        {!['comparison_grid', 'newsletter_digest', 'poster_spotlight', 'executive_card', 'infographic_card', 'testimonial', 'feature_list', 'hero_sheet', 'genui_html'].includes(layoutType) && (
          <div className="space-y-6">
            <header className="mb-6 text-center md:text-left">
              <h2 className="text-3xl font-black leading-tight text-white md:text-[2.2rem]">
                {headline}
              </h2>
              {spec.subheadline && (
                <p className="mt-3 text-sm leading-6 text-white/72 whitespace-pre-wrap">
                  {spec.subheadline}
                </p>
              )}
              {spec.summary && !spec.subheadline && (
                <p className="mt-3 text-sm leading-6 text-white/72 whitespace-pre-wrap">
                  {spec.summary}
                </p>
              )}
            </header>

            <div className="space-y-6">
              {Object.entries(spec).map(([key, value]) => {
                // Skip known top-level keys we already rendered
                if (['headline', 'subheadline', 'summary', 'eyebrow', 'accent_color', 'layout_type', 'dynamic_layout', 'intent_type', 'cta_label'].includes(key)) return null;

                // Array of Objects (e.g., Callouts, Features, Sections)
                if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
                  return (
                    <div key={key} className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-xl">
                      <h3 className={`mb-4 text-xs font-bold uppercase tracking-[0.18em] ${theme.text}`}>
                        {formatKeyName(key)}
                      </h3>
                      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {value.map((item: any, idx) => {
                          const parts = getItemParts(item);
                          return (
                            <div key={idx} className="rounded-xl border border-white/8 bg-black/20 p-3">
                              {parts.title && <p className="text-sm font-semibold">{parts.title}</p>}
                              {(parts.detail || parts.value) && <p className="mt-1 text-xs leading-5 text-white/70">{parts.detail || parts.value}</p>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                // Array of Strings (e.g., Bullets, Tags)
                if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
                  return (
                    <div key={key} className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl">
                      <h3 className={`mb-3 text-xs font-bold uppercase tracking-[0.18em] ${theme.text}`}>
                        {formatKeyName(key)}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {value.map((item, idx) => (
                          <span key={idx} className={`rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-white/80`}>
                            {String(item)}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                }

                // Flat String or Number (e.g., Quote, Date, Paragraphs)
                if (typeof value === 'string' || typeof value === 'number') {
                  const strValue = String(value);
                  const isLongText = strValue.length > 80 || key.toLowerCase().includes('body') || key.toLowerCase().includes('content') || key.toLowerCase().includes('text');
                  const isQuote = key.toLowerCase().includes('quote') || key.toLowerCase().includes('testimonial');

                  if (isQuote) {
                    return (
                      <blockquote key={key} className="rounded-[28px] border border-white/10 bg-white/8 p-6 text-left backdrop-blur-xl">
                        <div className="mb-3 text-[10px] font-bold uppercase tracking-wider text-white/50">{formatKeyName(key)}</div>
                        <p className="text-xl font-bold leading-relaxed">"{strValue}"</p>
                      </blockquote>
                    );
                  }

                  if (isLongText) {
                    return (
                      <div key={key} className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-xl">
                        <h3 className={`mb-3 text-xs font-bold uppercase tracking-[0.18em] ${theme.text}`}>
                          {formatKeyName(key)}
                        </h3>
                        <p className="text-sm leading-relaxed text-white/80 whitespace-pre-wrap">{strValue}</p>
                      </div>
                    );
                  }

                  return (
                    <div key={key} className="flex items-center justify-between gap-4 rounded-xl border border-white/8 bg-black/10 px-4 py-3">
                      <span className="text-xs font-semibold uppercase tracking-wider text-white/50">{formatKeyName(key)}</span>
                      <span className="text-sm font-medium text-white/90 text-right">{value}</span>
                    </div>
                  );
                }

                return null;
              })}
            </div>

            {spec.cta_label && (
              <div className="mt-8 text-center md:text-left">
                <div className={`inline-flex cursor-pointer rounded-full bg-gradient-to-r px-6 py-3 text-xs font-black uppercase tracking-[0.2em] text-white shadow-lg transition-transform hover:scale-105 ${theme.button}`}>
                  {spec.cta_label}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return shell;
}

export const UniversalPreview: React.FC<UniversalPreviewProps> = ({
  content_type,
  content_body,
  subject,
  piece,
}) => {
  const typeStr = String(content_type || '').toLowerCase().trim();
  const subjectStr = String(subject || '').toLowerCase().trim();
  const dynamicLayout = parseDynamicLayout(content_body, piece);

  const isLinkedIn =
    (typeStr.includes('linkedin') ||
      typeStr.includes('social') ||
      typeStr.includes('facebook') ||
      typeStr.includes('instagram') ||
      typeStr.includes('post') ||
      typeStr.includes('feed') ||
      typeStr.includes('threads') ||
      typeStr.includes('page') ||
      typeStr.includes('status') ||
      typeStr.includes('timeline')) &&
    !typeStr.includes('poster');

  const isPressRelease =
    typeStr.includes('press') ||
    typeStr.includes('release') ||
    typeStr.includes('pr') ||
    typeStr.includes('news') ||
    typeStr.includes('statement') ||
    typeStr.includes('announcement') ||
    subjectStr.includes('press release') ||
    subjectStr.includes('news release');

  const isEmail =
    typeStr.includes('email') ||
    typeStr.includes('newsletter') ||
    typeStr.includes('mail') ||
    typeStr.includes('outreach') ||
    typeStr.includes('blast') ||
    typeStr.includes('letter') ||
    typeStr.includes('digest') ||
    typeStr.includes('subscriber') ||
    typeStr.includes('campaign') ||
    typeStr.includes('sms') ||
    typeStr.includes('text message') ||
    typeStr.includes('whatsapp') ||
    typeStr.includes('dm') ||
    typeStr.includes('opener') ||
    typeStr.includes('notification');

  const isX =
    typeStr.includes('twitter') ||
    typeStr.includes('tweet') ||
    typeStr.includes('microblog') ||
    typeStr.includes('thread') ||
    typeStr.includes('x') ||
    typeStr.includes('shoutout') ||
    typeStr.includes('update');

  const isPoster =
    typeStr.includes('poster') ||
    typeStr.includes('flyer') ||
    typeStr.includes('banner') ||
    typeStr.includes('print') ||
    typeStr.includes('pdf') ||
    typeStr.includes('deck') ||
    typeStr.includes('slide') ||
    typeStr.includes('brochure') ||
    typeStr.includes('graphics') ||
    typeStr.includes('visual') ||
    typeStr.includes('image') ||
    typeStr.includes('presentation') ||
    typeStr.includes('signage') ||
    typeStr.includes('advertisement') ||
    typeStr.includes('postcard') ||
    typeStr.includes('billboard') ||
    typeStr.includes('thumbnail') ||
    subjectStr.includes('poster') ||
    subjectStr.includes('flyer') ||
    subjectStr.includes('banner') ||
    subjectStr.includes('infographic');

  const isBlog =
    typeStr.includes('blog') ||
    typeStr.includes('solution_brief') ||
    typeStr.includes('solution brief') ||
    typeStr.includes('article') ||
    typeStr.includes('whitepaper') ||
    typeStr.includes('case study') ||
    typeStr.includes('editorial') ||
    typeStr.includes('content') ||
    typeStr.includes('brief') ||
    typeStr.includes('paper') ||
    typeStr.includes('guide') ||
    typeStr.includes('manual') ||
    typeStr.includes('transcript') ||
    typeStr.includes('script') ||
    typeStr.includes('essay');

  const [isTruncated, setIsTruncated] = useState(true);
  const [likeCount, setLikeCount] = useState(42);
  const [isLiked, setIsLiked] = useState(false);

  const handleLikeToggle = () => {
    setIsLiked((prev) => !prev);
    setLikeCount((prev) => (isLiked ? prev - 1 : prev + 1));
  };

  const isStringContent = typeof content_body === 'string';
  const stringLength = isStringContent ? content_body.length : 500;
  const needsTruncation = stringLength > 140;

  // If we have a dynamic layout, ALWAYS use it — the AI generated
  // custom HTML for this content type (poster, press release, article, anything).
  if (dynamicLayout) {
    return renderDynamicLayout(dynamicLayout, subject, piece?.claim_mappings);
  }

  if (isLinkedIn) {
    return (
      <div
        className="w-full overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-md transition-all duration-200"
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}
      >
        <div className="flex items-start justify-between p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-tr from-sky-600 to-indigo-600 text-sm font-bold text-white shadow-inner">
              AM
            </div>
            <div className="leading-tight">
              <div className="flex items-center gap-1">
                <span className="cursor-pointer text-sm font-semibold text-gray-900 hover:text-sky-600">
                  Alex Morgan
                </span>
                <span className="text-xs text-gray-500">• 1st</span>
              </div>
              <p className="mt-0.5 text-xs font-normal text-gray-500">
                Software Engineer @ Cosellus
              </p>
              <div className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-400">
                <span>1d</span>
                <span>•</span>
                <span>Edited</span>
                <span>•</span>
                <Globe className="h-3 w-3" />
              </div>
            </div>
          </div>
          <button className="rounded-full p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-600">
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 pb-3 text-sm font-normal leading-relaxed tracking-wide text-gray-800">
          {isStringContent ? (
            <div>
              <p className="whitespace-pre-wrap">
                {isTruncated && needsTruncation ? content_body.slice(0, 140) : content_body}
                {isTruncated && needsTruncation && <span className="text-gray-500">...</span>}
              </p>
              {needsTruncation && (
                <button
                  onClick={() => setIsTruncated((prev) => !prev)}
                  className="mt-1 inline-block text-xs font-semibold text-gray-500 hover:text-sky-700 hover:underline"
                >
                  {isTruncated ? '...see more' : 'see less'}
                </button>
              )}
            </div>
          ) : (
            <div className="relative">
              <div className={`overflow-hidden transition-all duration-300 ${isTruncated ? 'max-h-[110px]' : 'max-h-none'}`}>
                {content_body}
              </div>
              {isTruncated ? (
                <div className="absolute bottom-0 left-0 right-0 flex h-10 items-end justify-end bg-gradient-to-t from-white via-white/80 to-transparent pr-1">
                  <button
                    onClick={() => setIsTruncated(false)}
                    className="relative z-10 bg-white pl-2 text-xs font-semibold text-gray-500 hover:text-sky-700 hover:underline"
                  >
                    ...see more
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsTruncated(true)}
                  className="mt-2 block text-xs font-semibold text-gray-500 hover:text-sky-700 hover:underline"
                >
                  see less
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-1.5 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-sky-600 text-[9px] font-bold text-white">👍</span>
            <span className="-ml-2 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">♥</span>
            <span className="ml-1">{likeCount}</span>
          </div>
          <span>5 comments • 2 reposts</span>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-gray-100 bg-gray-50/30 px-4 py-1.5">
          <button
            onClick={handleLikeToggle}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-xs font-medium transition-colors ${isLiked ? 'bg-sky-50/50 text-sky-600' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            <ThumbsUp className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
            <span>Like</span>
          </button>
          <button className="flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100">
            <MessageSquare className="h-4 w-4" />
            <span>Comment</span>
          </button>
          <button className="flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100">
            <Repeat className="h-4 w-4" />
            <span>Repost</span>
          </button>
          <button className="flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100">
            <Send className="h-4 w-4" />
            <span>Send</span>
          </button>
        </div>
      </div>
    );
  }

  if (isEmail) {
    const isPremiumTemplate = isStringContent && (
      String(content_body).includes('<style') || String(content_body).includes('<div')
    );

    if (isPremiumTemplate) {
      return (
        <div className="w-full genui-html-wrapper overflow-hidden rounded-xl" style={{ contain: 'layout' }}>
          <div dangerouslySetInnerHTML={{ __html: String(content_body) }} />
        </div>
      );
    }

    return (
      <div
        className="flex w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
        style={{ fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif' }}
      >
        <div className="flex flex-col gap-2 border-b border-gray-100 bg-gray-50/80 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-full bg-[#ff5f56] shadow-sm" />
              <span className="inline-block h-3 w-3 rounded-full bg-[#ffbd2e] shadow-sm" />
              <span className="inline-block h-3 w-3 rounded-full bg-[#27c93f] shadow-sm" />
            </div>
            <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400">Secure Message</span>
          </div>

          <div className="mt-1 space-y-1 text-xs text-gray-600">
            <div className="flex items-center">
              <span className="w-12 select-none text-gray-400">From:</span>
              <span className="font-semibold text-gray-800">Partnership Team</span>
              <span className="ml-1 text-[11px] text-gray-400">&lt;partners@cosellus.com&gt;</span>
            </div>
            <div className="flex items-center">
              <span className="w-12 select-none text-gray-400">To:</span>
              <span className="rounded border border-gray-100 bg-white px-1.5 py-0.5 text-[11px] text-gray-700">
                Target Decision Maker
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-baseline gap-2 border-b border-gray-50 bg-white px-4 py-3">
          <span className="select-none text-xs font-bold uppercase tracking-wide text-gray-400">Subject:</span>
          <h4 className="flex-1 truncate text-sm font-bold text-gray-900">
            {subject || 'Enterprise Partnership Strategic Opportunity'}
          </h4>
        </div>

        <div className="min-h-[180px] flex-1 overflow-y-auto bg-white p-4 text-sm leading-relaxed text-gray-800">
          {isStringContent ? (
            String(content_body).includes('<style') || String(content_body).includes('<div') ? (
              <div dangerouslySetInnerHTML={{ __html: String(content_body) }} />
            ) : (
              <div className="whitespace-pre-wrap">{content_body}</div>
            )
          ) : (
            <div>{content_body}</div>
          )}
        </div>
      </div>
    );
  }

  if (isX) {
    return (
      <div
        className="w-full rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
        style={{ fontFamily: '"Chirp", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}
      >
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-800 text-xs font-bold text-white">
            CS
          </div>
          <div>
            <div className="flex items-center gap-1">
              <span className="cursor-pointer text-sm font-bold text-gray-900 hover:underline">
                Cosellus Engineering
              </span>
              <CheckCircle2 className="h-3.5 w-3.5 fill-current text-sky-500" />
            </div>
            <p className="text-xs text-gray-500">@cosellus_eng</p>
          </div>
        </div>

        <div className="mb-3 text-sm leading-normal text-gray-900">
          {isStringContent ? <p className="whitespace-pre-wrap">{content_body}</p> : <div>{content_body}</div>}
        </div>

        <p className="mb-3 border-b border-gray-100 pb-3 text-xs text-gray-400">
          9:41 AM · May 13, 2026 · <span className="font-semibold text-gray-600">8.4K</span> Views
        </p>

        <div className="flex items-center justify-between px-1 text-gray-500">
          <button className="group flex items-center gap-1.5 transition-colors hover:text-sky-500">
            <div className="rounded-full p-1.5 group-hover:bg-sky-50">
              <MessageSquare className="h-4 w-4" />
            </div>
            <span className="text-xs">12</span>
          </button>
          <button className="group flex items-center gap-1.5 transition-colors hover:text-emerald-500">
            <div className="rounded-full p-1.5 group-hover:bg-emerald-50">
              <Repeat className="h-4 w-4" />
            </div>
            <span className="text-xs">4</span>
          </button>
          <button
            onClick={handleLikeToggle}
            className={`group flex items-center gap-1.5 transition-colors ${isLiked ? 'text-pink-600' : 'hover:text-pink-600'}`}
          >
            <div className="rounded-full p-1.5 group-hover:bg-pink-50">
              <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
            </div>
            <span className="text-xs">{likeCount}</span>
          </button>
          <button className="group flex items-center gap-1.5 transition-colors hover:text-sky-500">
            <div className="rounded-full p-1.5 group-hover:bg-sky-50">
              <Bookmark className="h-4 w-4" />
            </div>
          </button>
          <button className="group flex items-center gap-1.5 transition-colors hover:text-sky-500">
            <div className="rounded-full p-1.5 group-hover:bg-sky-50">
              <Share2 className="h-4 w-4" />
            </div>
          </button>
        </div>
      </div>
    );
  }

  if (isPoster) {
    return (
      <div
        className="relative w-full overflow-hidden rounded-xl border border-slate-800 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white shadow-2xl"
        style={{ fontFamily: 'Outfit, system-ui, sans-serif', minHeight: '520px' }}
      >
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#80808014_1px,transparent_1px),linear-gradient(to_bottom,#80808014_1px,transparent_1px)] bg-[size:20px_20px]" />
        <div className="pointer-events-none absolute left-1/2 top-0 h-64 w-3/4 -translate-x-1/2 rounded-full bg-indigo-500/10 blur-3xl" />

        <div className="relative z-10 flex items-center justify-between border-b border-slate-800 bg-slate-950/60 px-6 py-4 backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 animate-pulse rounded-full bg-indigo-400" />
            <span className="flex items-center gap-1 rounded border border-indigo-500/30 bg-indigo-500/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-300">
              <Printer className="h-3 w-3" />
              Event Poster
            </span>
          </div>
          <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            <Sparkles className="h-3 w-3 text-indigo-400" />
            Hi-Res Layout Draft
          </span>
        </div>

        <div className="relative z-10 flex min-h-[440px] flex-col items-center justify-between p-8 text-center">
          <div className="pointer-events-none absolute left-4 top-4 h-4 w-4 border-l-2 border-t-2 border-slate-700" />
          <div className="pointer-events-none absolute right-4 top-4 h-4 w-4 border-r-2 border-t-2 border-slate-700" />
          <div className="pointer-events-none absolute bottom-4 left-4 h-4 w-4 border-b-2 border-l-2 border-slate-700" />
          <div className="pointer-events-none absolute bottom-4 right-4 h-4 w-4 border-b-2 border-r-2 border-slate-700" />

          {subject && (
            <h1 className="mb-4 mt-4 max-w-xl bg-gradient-to-r from-white via-indigo-100 to-sky-200 bg-clip-text text-xl font-black uppercase leading-tight text-transparent md:text-2xl">
              {subject}
            </h1>
          )}

          <div className="my-3 w-full max-w-2xl rounded-xl border border-slate-800/80 bg-slate-900/60 p-6 text-left text-sm leading-relaxed text-slate-300 shadow-inner backdrop-blur-md">
            {isStringContent ? <div className="whitespace-pre-wrap text-slate-200">{content_body}</div> : <div className="space-y-1">{content_body}</div>}
          </div>

          <div className="mb-2 mt-4 w-full max-w-md">
            <div className="cursor-pointer rounded-lg border border-white/10 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 px-6 py-3 text-center text-xs font-extrabold uppercase tracking-wider text-white shadow-lg transition-all hover:opacity-90">
              Scan QR Code or Click to Learn More
            </div>
            <p className="mt-2 text-[10px] font-medium tracking-wide text-slate-500">
              Dimensions: 24" x 36" (300 DPI high-fidelity print asset)
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isPressRelease) {
    return (
      <div
        className="w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-md transition-all duration-200"
        style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}
      >
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-6 py-4">
          <div className="flex items-center gap-2">
            <span className="rounded border border-sky-300 bg-sky-50 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-widest text-sky-700">
              Press Release
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              For Immediate Release
            </span>
          </div>
          <span className="text-[10px] font-semibold text-slate-400">
            Official Announcement
          </span>
        </div>

        <div className="p-8">
          <div className="mb-6 text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">Cosellus Media Relations</p>
            {subject && (
              <h1 className="text-2xl font-black leading-tight text-slate-900 md:text-3xl font-serif">
                {subject}
              </h1>
            )}
            <div className="mx-auto mt-4 h-0.5 w-16 bg-slate-200" />
          </div>

          <div className="space-y-4 text-base leading-relaxed text-slate-800">
            {isStringContent ? (
              <div className="whitespace-pre-wrap">
                <span className="font-sans font-bold text-slate-900 tracking-wide text-sm mr-1">
                  SAN FRANCISCO, CA — {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} — 
                </span>
                {content_body}
              </div>
            ) : (
              <div>{content_body}</div>
            )}
          </div>

          <div className="mt-8 border-t border-slate-100 pt-6 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">###</p>
            <p className="mt-2 text-[10px] text-slate-400 font-sans tracking-wide">
              About Cosellus: Co-selling intelligence and automated strategic marketing orchestration.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      <div className="flex items-center justify-between bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-4 text-white">
        <div className="flex items-center gap-2">
          <span className="rounded border border-emerald-500/30 bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400">
            {isBlog ? 'Solution Brief' : 'Article'}
          </span>
          <span className="text-xs text-slate-400">• 3 min read</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-300">
          <User className="h-3.5 w-3.5" />
          <span>Cosellus Editorial</span>
        </div>
      </div>

      <div className="p-6">
        {subject && (
          <h2 className="mb-4 border-b border-gray-100 pb-3 text-lg font-bold leading-tight text-gray-900">
            {subject}
          </h2>
        )}
        <div className="space-y-3 text-sm leading-relaxed text-gray-700">
          {isStringContent ? <div className="whitespace-pre-wrap">{content_body}</div> : <div>{content_body}</div>}
        </div>
      </div>
    </div>
  );
};

export default UniversalPreview;
